import React, { useCallback, useMemo, memo, useLayoutEffect, useRef } from 'react';
import { useAuthNavigation } from '../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Image, Platform } from 'react-native';
import { Clickable } from '../../components/common/Clickable';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation, getApiText, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';

import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { useSharedValue, useAnimatedStyle, withSpring, withSequence, withTiming, cancelAnimation, interpolate, interpolateColor, Extrapolation, runOnJS, runOnUI } from 'react-native-reanimated';

import { MenuItemData } from '../../data/domains/menu';
import { resolveMenuItemScaledCarbsDisplay } from '../../data/domains/menu/scaledCarbs';
import { useNutritionUiEnabled } from '../../data/domains/config';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { useScreenTheme } from '../../shared/theme/useScreenTheme';
import { DASH_STUDIO_RADIUS, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { triggerTactileHaptic, triggerMenuItemHaptic, useJellyGesture, useNudgeGesture, resolveMenuFlowCardTap, pulseMenuCardImagePress, type MenuItemHealthHaptic } from './tactile';
import {
  MEAL_SNACK_TAG_BORDER_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
  MEAL_SNACK_TAG_EXPAND_ICON_SIZE,
  MEAL_SNACK_TAG_OVERLAY_BG,
  MEAL_SNACK_TAG_OVERLAY_BG_HALF,
  MEAL_SNACK_TAG_SHORT_SIZE,
  estimateMealSnackTagLongWidth,
} from './menuFlowItemCardShared';

export {
  MEAL_SNACK_TAG_BORDER_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
  MEAL_SNACK_TAG_EXPAND_ICON_SIZE,
  MEAL_SNACK_TAG_OVERLAY_BG,
  MEAL_SNACK_TAG_SHORT_SIZE,
  estimateMealSnackTagLongWidth,
} from './menuFlowItemCardShared';

// --- Pure helpers (module scope — never recreated per render) ---------------------------------

export function parseMacro(val: unknown): number {
  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
  return Number.isNaN(num) ? 0 : Math.round(num);
}

export function getHealthCodeFlags(item: MenuItemData): { isAllergy: boolean; isDislike: boolean; showBadge: boolean } {
  const code = typeof (item as { health_code?: number }).health_code === 'number' ? (item as { health_code: number }).health_code : 0;
  const isAllergy = code === 1 || code === 10;
  const isDislike = code === 2 || code === 20;
  const showBadge = code != null && code !== 0;
  return { isAllergy, isDislike, showBadge };
}

/** Border shared-value key: 0 default, 1 primary, 2 allergy, 3 dislike */
export function borderStateValue(isSelected: boolean, isAllergy: boolean, isDislike: boolean): 0 | 1 | 2 | 3 {
  if (!isSelected) {
    return 0;
  }
  if (isAllergy) {
    return 2;
  }
  if (isDislike) {
    return 3;
  }
  return 1;
}

export interface MenuFlowItemCardProps {
  /** When theme-driven colors change, this must change so memoized cards re-render. */
  themeStyleKey: string;
  item: MenuItemData;
  /** Section's builder id – use this for callbacks so the correct section is updated when the same item appears in multiple meal types. */
  menuBuilderId?: number;
  /** (menu_builder_id, itemId, isPlusClick) – menu_builder_id scopes the item to its section so the same dish in lunch vs snack is unique. */
  onQuantityChange: (menu_builder_id: number, itemId: number, isPlusClick: boolean) => void;
  mealsCount: number;
  snacksCount: number;
  isForceFixedMenu?: boolean;
  /** Browse-only menu (e.g. no subscription): block quantity and scaled-carbs edits. */
  selectionDisabled?: boolean;
  /** Fired when the user tries to change quantity or carbs while `selectionDisabled` (e.g. show a subscription prompt). */
  onSelectionDisabledInteraction?: () => void;
  onItemPress?: (item: MenuItemData) => void;
  isGroupLimitReached?: boolean;
  onLimitExceeded?: (message: string) => void;
  scrollGestureRef?: React.RefObject<unknown>;
  isVariableCarbs?: boolean;
  defaultScaledCarbs?: number;
  maxScaledCarbs?: number;
  onScaledCarbsChange?: (menu_builder_id: number, itemId: number, value: number) => void;
  /** Softer selection transitions (accessibility / menu list performance). */
  reduceMotion?: boolean;
}

type QuantitySource = 'card' | 'stepper';

export interface QuantityIntentContext {
  quantity: number;
  isForceFixedMenu: boolean;
  selectionDisabled: boolean;
  wouldExceedLimit: boolean;
  menuBuilderId: number;
  itemId: number;
  limitMessage: string;
  isAllergy: boolean;
  isDislike: boolean;
  onQuantityChange: (menu_builder_id: number, itemId: number, isPlusClick: boolean) => void;
  onSelectionDisabledInteraction?: () => void;
  onLimitExceeded?: (message: string) => void;
  triggerShake: (direction: 'left' | 'right') => void;
  /** Card-only: reset jelly active spring when qty 0 and user hits limit (matches legacy cancelAnimation + spring). */
  onCardQuantityZeroLimit?: () => void;
}

function menuItemHealthFromContext(ctx: QuantityIntentContext): MenuItemHealthHaptic {
  if (ctx.isAllergy) {
    return 'allergy';
  }
  if (ctx.isDislike) {
    return 'dislike';
  }
  return 'none';
}

/**
 * Unified quantity handling: fixed menu, browse-only, group limits, meal/snack caps.
 * Card tap: 0→1, 1→0, or shake at limit (qty > 1 only shakes when over limit). Stepper: standard +/- .
 */
export function applyQuantityIntent(intent: 'increment' | 'decrement', source: QuantitySource, touchDirection: 'left' | 'right', ctx: QuantityIntentContext): void {
  if (ctx.isForceFixedMenu) {
    return;
  }
  if (ctx.selectionDisabled) {
    ctx.onSelectionDisabledInteraction?.();
    return;
  }

  const health = menuItemHealthFromContext(ctx);

  if (intent === 'decrement') {
    if (ctx.quantity < 1) {
      return;
    }
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, false);
    return;
  }

  if (source === 'stepper') {
    if (ctx.wouldExceedLimit) {
      ctx.triggerShake('right');
      ctx.onLimitExceeded?.(ctx.limitMessage);
      return;
    }
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, true);
    return;
  }

  if (ctx.quantity === 0) {
    if (ctx.wouldExceedLimit) {
      ctx.onCardQuantityZeroLimit?.();
      ctx.triggerShake(touchDirection);
      ctx.onLimitExceeded?.(ctx.limitMessage);
      return;
    }
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, true);
    return;
  }
  if (ctx.quantity === 1) {
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, false);
    return;
  }
  if (ctx.wouldExceedLimit) {
    ctx.triggerShake(touchDirection);
  }
}

/** Card body height (excludes vertical margin). */
export const MENU_FLOW_ITEM_CARD_HEIGHT = 156;
/** Vertical margin above and below each card (`fullCardContainer`). */
export const MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL = 8;
/** Total FlatList row height: card + margins — use in `MenuFlowScreen` scroll/`getItemLayout` math. */
export const MENU_FLOW_ITEM_ROW_HEIGHT = MENU_FLOW_ITEM_CARD_HEIGHT + 2 * MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL;

/**
 * Selected-state border width. Always reserved in layout (transparent when unselected)
 * so image / title / macros do not shift when the border appears.
 */
export const MENU_FLOW_ITEM_CARD_BORDER_WIDTH = 1;
/** Inner content height inside the card border. */
export const MENU_FLOW_ITEM_CARD_INNER_HEIGHT =
  MENU_FLOW_ITEM_CARD_HEIGHT - 2 * MENU_FLOW_ITEM_CARD_BORDER_WIDTH;

/**
 * Single inset applied on the inner row (`cardTouchableRow`) — equal space from the card
 * edge to image (3 sides), content (title / carbs / macros / qty), and between image ↔ content.
 */
export const MENU_FLOW_ITEM_CARD_INSET = 10;
/** @deprecated Prefer `MENU_FLOW_ITEM_CARD_INSET` — same value. */
export const MENU_FLOW_ITEM_IMAGE_GAP = MENU_FLOW_ITEM_CARD_INSET;
/** @deprecated Prefer `MENU_FLOW_ITEM_CARD_INSET` — alias for older order/calendar imports. */
export const MENU_FLOW_ITEM_IMAGE_PADDING = MENU_FLOW_ITEM_CARD_INSET;

/** Square image side — fills inner height minus top/bottom row inset. */
export const MENU_FLOW_ITEM_IMAGE_WIDTH =
  MENU_FLOW_ITEM_CARD_INNER_HEIGHT - 2 * MENU_FLOW_ITEM_CARD_INSET;
const IMAGE_WIDTH = MENU_FLOW_ITEM_IMAGE_WIDTH;

/**
 * Image column width = image only. Outer inset + image↔content gap live on the row
 * (`padding` + `gap`), not on this column — keeps qty/carbs clear of the card border.
 */
export const MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH = MENU_FLOW_ITEM_IMAGE_WIDTH;
const IMAGE_SECTION_WIDTH = MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH;

/**
 * Extra padding for title + description only.
 * Does not inset scaled-carbs (top corner) or qty (bottom corner).
 */
export const MENU_FLOW_ITEM_CONTENT_PADDING_H = 0;
export const MENU_FLOW_ITEM_CONTENT_PADDING_V = 0;
/** Space between title and description — keep small so footer (macros / qty) stays inside card padding. */
export const MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP = 4;
export const MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP = 4;
export const MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT = 32;
export const MENU_FLOW_ITEM_MACRO_GAP = 6;
export const MENU_FLOW_ITEM_TITLE_MIN_HEIGHT = 22;
/** Unselected card image corner radius (`interpolate(activeState, [0,1], [21,16])`). */
export const MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS = 18;
export const MENU_FLOW_ITEM_BADGE_INSET = 5;
export const MENU_FLOW_ITEM_TITLE_FONT_SIZE = 16;
export const MENU_FLOW_ITEM_DESC_FONT_SIZE = 12;
export const MENU_FLOW_ITEM_MACRO_VALUE_FONT_SIZE = 12;
export const MENU_FLOW_ITEM_MACRO_LABEL_FONT_SIZE = 9;

/** Selection spring — damped so border/bg changes settle without bounce. */
const CARD_SPRING = { stiffness: 280, damping: 44, mass: 0.55 };
const CARD_TIMING = { duration: 180 };
const PULSE_SPRING = { stiffness: 380, damping: 32, mass: 0.5 };

const STEP_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 };

const AnimatedView = Animated.createAnimatedComponent(View);

function createMenuFlowItemCardStyles(c: ThemeColors, appearance: 'light' | 'dark') {
  return StyleSheet.create({
    fullCardContainer: {
      height: MENU_FLOW_ITEM_CARD_HEIGHT,
      minHeight: MENU_FLOW_ITEM_CARD_HEIGHT,
      marginVertical: MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
      width: '100%',
      /** Avoid `flex: 1` — on RN-web it becomes flex-basis:0 and crushes FlatList rows to ~border height. */
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'stretch',
      overflow: 'hidden',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
      borderColor: 'transparent',
      ...horizontalLtrDirectionStyle,
    },
    cardTouchableRow: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'stretch',
      /** One inset for image + content — keeps qty/carbs off the border and image off the edges. */
      padding: MENU_FLOW_ITEM_CARD_INSET,
      gap: MENU_FLOW_ITEM_CARD_INSET,
      ...horizontalLtrDirectionStyle,
    },
    imageInner: {
      overflow: 'hidden',
      width: IMAGE_WIDTH,
      height: IMAGE_WIDTH,
      flexShrink: 0,
    },
    imageSection: {
      width: IMAGE_SECTION_WIDTH,
      alignSelf: 'center',
      justifyContent: 'center',
    },
    image: { width: '100%', height: '100%' },
    detailExpandBtn: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      height: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderTopLeftRadius: MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
      borderBottomLeftRadius: 0,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 4,
    },
    badgeOverlay: {
      position: 'absolute',
      top: MENU_FLOW_ITEM_BADGE_INSET,
      left: MENU_FLOW_ITEM_BADGE_INSET,
      right: MENU_FLOW_ITEM_BADGE_INSET,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
    },
    badgeOverlayRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
      maxWidth: '70%',
    },
    tag: {
      backgroundColor: MEAL_SNACK_TAG_OVERLAY_BG,
      borderRadius: MEAL_SNACK_TAG_BORDER_RADIUS,
      position: 'relative',
      overflow: 'hidden',
      justifyContent: 'center',
      alignItems: 'center',
    },
    tagLabelLayer: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
    },
    healthCodeBadge: {
      backgroundColor: c.error,
      width: 16,
      height: 16,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    healthCodeBadgeText: {
      color: c.onError,
      fontSize: 12,
      fontWeight: '700',
    },
    selectionTag: {
      backgroundColor: c.primary,
      paddingHorizontal: 6,
      paddingVertical: 3,
    },
    tagText: {
      color: c.onPrimary,
      fontSize: 9,
      fontWeight: '800',
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    tagTextLong: {
      color: c.onPrimary,
      fontSize: 9,
      fontWeight: '800',
      textAlign: 'center',
      maxWidth: 120,
    },
    contentSection: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'space-between',
      overflow: 'hidden',
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      flexShrink: 0,
      gap: 6,
      ...horizontalLtrDirectionStyle,
    },
    /** Title only — CONTENT_PADDING_H/V apply here, not to the carbs corner control. */
    titleCell: {
      flex: 1,
      minWidth: 0,
      paddingTop: MENU_FLOW_ITEM_CONTENT_PADDING_V,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
    },
    title: {
      width: '100%',
      fontSize: MENU_FLOW_ITEM_TITLE_FONT_SIZE,
      fontWeight: '600',
      color: c.text,
      minHeight: MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
    },
    scaledCarbsColumn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      alignSelf: 'flex-start',
      flexShrink: 0,
      gap: 4,
    },
    scaledCarbValueContainer: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0,
      flexShrink: 0,
    },
    scaledCarbsAmountText: {
      flexShrink: 0,
      fontSize: 10,
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
      ...Platform.select({
        android: { includeFontPadding: false },
        default: {},
      }),
    },
    scaledCarbsTitleText: {
      fontSize: 8,
      fontWeight: '400',
      color: c.text,
      textAlign: 'center',
      ...Platform.select({
        android: { includeFontPadding: false },
        default: {},
      }),
    },
    scaledCarbsBtn: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: c.primary,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      flexShrink: 0,
    },
    scaledCarbsBtnInner: {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    scaledCarbsBtnText: {
      color: c.onPrimary,
      fontSize: 11,
      fontWeight: '700',
      lineHeight: 12,
      textAlign: 'center',
      ...Platform.select({
        android: { includeFontPadding: false, textAlignVertical: 'center' as const },
        default: {},
      }),
    },
    textArea: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      overflow: 'hidden',
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      paddingBottom: MENU_FLOW_ITEM_CONTENT_PADDING_V,
    },
    description: {
      width: '100%',
      alignSelf: 'stretch',
      fontSize: MENU_FLOW_ITEM_DESC_FONT_SIZE,
      color: c.textSecondary,
      marginTop: MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
      flexShrink: 1,
    },
    bottomRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      paddingTop: MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP,
      minHeight: MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
      flexShrink: 0,
      width: '100%',
      ...horizontalLtrDirectionStyle,
    },
    bottomRowRight: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexShrink: 0,
      marginLeft: 6,
    },
    quantityCounterGroup: {
      height: 28,
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: '100%',
    },
    macroGroup: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: MENU_FLOW_ITEM_MACRO_GAP,
      flex: 1,
      minWidth: 0,
      ...horizontalLtrDirectionStyle,
    },
    macroPoint: { alignItems: 'flex-start', position: 'relative' },
    dot: { width: 12, height: 2, borderRadius: 1, marginBottom: 2 },
    macroValue: { fontSize: 12, fontWeight: '700', color: c.text },
    macroLabel: { fontSize: 9, color: c.textLight },
    quantityCounter: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      borderRadius: 12,
      height: 28,
    },
    quantityStepperSide: {
      zIndex: 2,
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
    },
    quantityCounterBg: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      right: 0,
      backgroundColor: c.primary,
      borderRadius: 16,
      height: 28,
      zIndex: 1,
    },
    quantityItem: {
      color: c.onPrimary,
      fontSize: 16,
      fontWeight: '800',
      textAlign: 'center',
      zIndex: 2,
      width: 28,
    },
  });
}

type MenuFlowItemCardStyles = ReturnType<typeof createMenuFlowItemCardStyles>;

const MacroPoint = memo(({ value, label, color, cardStyles }: { value: number; label?: string; color: string; cardStyles: MenuFlowItemCardStyles }) => (
  <View style={cardStyles.macroPoint}>
    <View style={[cardStyles.dot, { backgroundColor: color }]} />
    <Text style={cardStyles.macroValue}>
      {formatNumber(value)}
      <Text style={cardStyles.macroLabel}>{label}</Text>
    </Text>
  </View>
));

function menuFlowItemCardPropsAreEqual(prev: MenuFlowItemCardProps, next: MenuFlowItemCardProps): boolean {
  if (prev.themeStyleKey !== next.themeStyleKey) {
    return false;
  }
  if (prev.menuBuilderId !== next.menuBuilderId) {
    return false;
  }
  if (prev.item.id !== next.item.id) {
    return false;
  }
  if (prev.item.quantity !== next.item.quantity) {
    return false;
  }
  if (prev.item.is_selected !== next.item.is_selected) {
    return false;
  }
  if (prev.selectionDisabled !== next.selectionDisabled) {
    return false;
  }
  if ((prev.item as { scaled_carbs?: number }).scaled_carbs !== (next.item as { scaled_carbs?: number }).scaled_carbs) {
    return false;
  }
  if ((prev.item as { health_code?: number }).health_code !== (next.item as { health_code?: number }).health_code) {
    return false;
  }
  if (prev.isForceFixedMenu !== next.isForceFixedMenu) {
    return false;
  }
  if (prev.isGroupLimitReached !== next.isGroupLimitReached) {
    return false;
  }
  if (prev.mealsCount !== next.mealsCount || prev.snacksCount !== next.snacksCount) {
    return false;
  }
  if (prev.isVariableCarbs !== next.isVariableCarbs) {
    return false;
  }
  if (prev.defaultScaledCarbs !== next.defaultScaledCarbs || prev.maxScaledCarbs !== next.maxScaledCarbs) {
    return false;
  }
  if (prev.reduceMotion !== next.reduceMotion) {
    return false;
  }
  return true;
}

export const MenuFlowItemCard: React.FC<MenuFlowItemCardProps> = memo(
  ({
    themeStyleKey: _themeStyleKey,
    item,
    menuBuilderId: menuBuilderIdProp,
    onQuantityChange,
    mealsCount,
    snacksCount,
    isForceFixedMenu = false,
    selectionDisabled = false,
    onSelectionDisabledInteraction,
    onItemPress,
    isGroupLimitReached,
    onLimitExceeded,
    scrollGestureRef,
    isVariableCarbs = false,
    defaultScaledCarbs = 50,
    maxScaledCarbs = 100,
    onScaledCarbsChange,
    reduceMotion = false,
  }) => {
    const navigation = useAuthNavigation();
    const { t } = useTranslation();
    const nutrition_ui_enabled = useNutritionUiEnabled();
    const rtl = useRtl();
    const { colors: themeColors, appearance } = useScreenTheme();
    const c = themeColors ?? appTheme.colors;
    const primaryC = c.primary;
    const errorC = c.error;
    const warningC = c.warning;
    const surfaceC = c.surface;
    const surfaceAltC = c.surfaceLight ?? c.surfaceFade;
    const shadowC = c.shadowColor;
    const styles = useMemo(() => createMenuFlowItemCardStyles(c, appearance), [c, appearance]);
    const menuBuilderId = typeof menuBuilderIdProp === 'number' ? Number(menuBuilderIdProp) : Number(item.menu_builder_id ?? item.food_type_id ?? 0);
    const isSelected = item.is_selected || item.quantity > 0;
    const { isAllergy, isDislike, showBadge } = useMemo(() => getHealthCodeFlags(item), [item]);
    const isMeal = item.food_type_category?.toLowerCase() === 'meal';
    const limitExceededMessage = isMeal ? t('menu.maxMealLimitReached') : t('menu.maxSnackLimitReached');

    const activeState = useSharedValue(isSelected ? 1 : 0);
    /** Discrete health flags for border color (updated synchronously; border color blends via `activeState` only). */
    const isAllergySV = useSharedValue(isAllergy ? 1 : 0);
    const isDislikeSV = useSharedValue(isDislike ? 1 : 0);
    const shakeOffset = useSharedValue(0);
    const did_sync_selection_spring_ref = useRef(false);
    const fullWidthRef = useSharedValue(0);
    const contentWidthRef = useSharedValue(200);
    const imagePressScale = useSharedValue(1);
    const plusPulseScale = useSharedValue(1);
    const minusPulseScale = useSharedValue(1);
    const tagLongTotalWidth = useSharedValue(72);

    const mealSnackLong = isMeal ? t('menu.meal') : t('menu.snack');
    const stableTagLongWidth = useMemo(() => estimateMealSnackTagLongWidth(mealSnackLong), [mealSnackLong]);
    const selection_n = Number(item.selection_number ?? 0);
    const show_selection_tag = isMeal && selection_n > 0;

    useLayoutEffect(() => {
      tagLongTotalWidth.value = stableTagLongWidth;
    }, [stableTagLongWidth, tagLongTotalWidth]);

    useLayoutEffect(() => {
      isAllergySV.value = isAllergy ? 1 : 0;
      isDislikeSV.value = isDislike ? 1 : 0;
    }, [isAllergy, isDislike, isAllergySV, isDislikeSV]);

    /** UI-thread spring for selection progress only — avoids JS `useEffect` one-frame lag and bad `borderState` springs between 0–3. */
    useLayoutEffect(() => {
      if (!did_sync_selection_spring_ref.current) {
        did_sync_selection_spring_ref.current = true;
        return;
      }
      const target = isSelected ? 1 : 0;
      runOnUI((t: number) => {
        'worklet';
        cancelAnimation(activeState);
        activeState.value = reduceMotion ? withTiming(t, CARD_TIMING) : withSpring(t, CARD_SPRING);
      })(target);
    }, [isSelected, activeState, reduceMotion]);

    const triggerShake = useCallback(
      (direction: 'left' | 'right') => {
        const health: MenuItemHealthHaptic = isAllergy ? 'allergy' : isDislike ? 'dislike' : 'none';
        triggerMenuItemHaptic('shake', health);
        const offset = direction === 'left' ? -0.01 : 0.01;
        runOnUI((off: number) => {
          'worklet';
        cancelAnimation(shakeOffset);
        shakeOffset.value = withSequence(
          withTiming(off, { duration: 30 }),
          withTiming(-off, { duration: 30 }),
          withTiming(off, { duration: 30 }),
          withTiming(0, { duration: 40 }),
        );
        })(offset);
      },
      [shakeOffset, isAllergy, isDislike],
    );

    const wouldExceedLimit = useCallback(() => {
      const limit = isMeal ? mealsCount : snacksCount;
      return item.quantity >= limit || isGroupLimitReached === true;
    }, [item.quantity, isMeal, mealsCount, snacksCount, isGroupLimitReached]);

    const resetActiveVisualOnCardLimit = useCallback(() => {
      runOnUI(() => {
        'worklet';
        cancelAnimation(activeState);
        activeState.value = reduceMotion ? withTiming(0, CARD_TIMING) : withSpring(0, CARD_SPRING);
      })();
    }, [activeState, reduceMotion]);

    const buildQuantityContext = useCallback((): QuantityIntentContext => {
      return {
        quantity: item.quantity,
        isForceFixedMenu,
        selectionDisabled,
        wouldExceedLimit: wouldExceedLimit(),
        menuBuilderId,
        itemId: item.id,
        limitMessage: limitExceededMessage,
        isAllergy,
        isDislike,
        onQuantityChange,
        onSelectionDisabledInteraction,
        onLimitExceeded,
        triggerShake,
        onCardQuantityZeroLimit: resetActiveVisualOnCardLimit,
      };
    }, [
      item.quantity,
      isForceFixedMenu,
      selectionDisabled,
      wouldExceedLimit,
      menuBuilderId,
      item.id,
      limitExceededMessage,
      isAllergy,
      isDislike,
      onQuantityChange,
      onSelectionDisabledInteraction,
      onLimitExceeded,
      triggerShake,
      resetActiveVisualOnCardLimit,
    ]);

    const handleOpenDetail = useCallback(() => {
      triggerTactileHaptic('press');
      if (onItemPress) {
        onItemPress(item);
      } else {
        navigation.navigate('FoodItemDetail', { item });
      }
    }, [item, onItemPress, navigation]);

    const expandTap = useMemo(
      () =>
        Gesture.Tap()
          .maxDistance(12)
          .onEnd(() => {
            runOnJS(handleOpenDetail)();
          }),
      [handleOpenDetail],
    );

    const handleCardEnd = useCallback(
      (touchX: number, contentWidth: number) => {
        const full_width = fullWidthRef.value;
        const { region, direction } = resolveMenuFlowCardTap(touchX, full_width, contentWidth, false);
        if (region === 'image') {
          pulseMenuCardImagePress(imagePressScale);
        }
        triggerTactileHaptic('press');
        applyQuantityIntent('increment', 'card', direction, buildQuantityContext());
      },
      [imagePressScale, buildQuantityContext, fullWidthRef, rtl],
    );

    const { gesture: cardTap, jellySharedValues } = useJellyGesture({
      fullWidthRef,
      contentWidthRef,
      height: MENU_FLOW_ITEM_CARD_HEIGHT,
      onEnd: handleCardEnd,
      scrollGestureRef,
      /** Pan follower runs ~8 springs per frame — kills scroll FPS inside `FlatList`. Tap jelly only when nested on Menu Flow. */
      enablePanJelly: scrollGestureRef == null,
      /** In the menu list, skip tap skew/scale so scroll stays smooth; card tap logic unchanged. */
      lightTapFeedback: scrollGestureRef != null,
    });

    const handlePlus = useCallback(() => {
      runOnUI(() => {
        'worklet';
        cancelAnimation(plusPulseScale);
        plusPulseScale.value = withSequence(withTiming(1.06, { duration: 60 }), withSpring(1, PULSE_SPRING));
      })();
      applyQuantityIntent('increment', 'stepper', 'right', buildQuantityContext());
    }, [plusPulseScale, buildQuantityContext]);

    const handleMinus = useCallback(() => {
      runOnUI(() => {
        'worklet';
        cancelAnimation(minusPulseScale);
        minusPulseScale.value = withSequence(withTiming(1.06, { duration: 60 }), withSpring(1, PULSE_SPRING));
      })();
      applyQuantityIntent('decrement', 'stepper', 'left', buildQuantityContext());
    }, [minusPulseScale, buildQuantityContext]);

    const minusNudge = useNudgeGesture({ direction: 'left', onEnd: handleMinus, hitSlop: STEP_HIT_SLOP });
    const plusNudge = useNudgeGesture({ direction: 'right', onEnd: handlePlus, hitSlop: STEP_HIT_SLOP });

    const displayed_scaled_carbs = resolveMenuItemScaledCarbsDisplay(
      item as { scaled_carbs?: number; quantity?: number; is_selected?: boolean },
      defaultScaledCarbs,
    );

    const bumpScaledCarbs = useCallback(
      (delta: number) => {
        if (selectionDisabled) {
          onSelectionDisabledInteraction?.();
          return;
        }
        if (!onScaledCarbsChange) {
          return;
        }
        const current = displayed_scaled_carbs;
        const next = Math.max(0, Math.min(maxScaledCarbs || 999, current + delta));
        onScaledCarbsChange(menuBuilderId, item.id, next);
      },
      [selectionDisabled, onSelectionDisabledInteraction, onScaledCarbsChange, displayed_scaled_carbs, maxScaledCarbs, menuBuilderId, item.id],
    );

    const jellyCardStyle = useAnimatedStyle(() => {
      const { jellyScaleX, jellyScaleY } = jellySharedValues;
      const shake = shakeOffset.value;
      const jellyOn = Math.abs(shake) < 0.05;
      const sx = (1 + shake) * (jellyOn ? jellyScaleX.value : 1);
      const sy = (1 + shake) * (jellyOn ? jellyScaleY.value : 1);
      const t = activeState.value;
      const selected_border = isAllergySV.value > 0.5 ? errorC : isDislikeSV.value > 0.5 ? warningC : primaryC;
      const selected = t > 0.5;
      return {
        transform: [{ scaleX: sx }, { scaleY: sy }],
        borderRadius: interpolate(t, [0, 1], [25, 20]),
        /** Always reserve width; transparent when unselected so only selected cards show a border. */
        borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
        borderColor: interpolateColor(t, [0, 1], ['rgba(0,0,0,0)', selected_border]),
        backgroundColor: interpolateColor(t, [0, 1], [surfaceC, surfaceAltC]),
        ...(Platform.OS === 'ios'
          ? {
              shadowColor: shadowC,
              shadowOffset: { width: 0, height: selected ? 2 : 0 },
              shadowOpacity: selected ? 0.06 : 0,
              shadowRadius: selected ? 4 : 0,
            }
          : { elevation: selected ? 2 : 0 }),
      };
    }, [primaryC, errorC, warningC, surfaceC, surfaceAltC, shadowC, jellySharedValues]);

    const imageBorderRadiusStyle = useAnimatedStyle(() => ({
      borderRadius: interpolate(activeState.value, [0, 1], [MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS, 14]),
    }));

    const imagePressStyle = useAnimatedStyle(() => ({
      transform: [{ scale: imagePressScale.value }],
    }));

    const expandBtnStyle = useAnimatedStyle(() => ({
      backgroundColor: surfaceC,
    }));
    const selectionBadgeStyle = useAnimatedStyle(() => ({
      opacity: activeState.value,
      transform: [{ scale: interpolate(activeState.value, [0, 1], [0.88, 1], Extrapolation.CLAMP) }],
    }));

    const tagMorphStyle = useAnimatedStyle(() => ({
      width: interpolate(activeState.value, [0, 1], [MEAL_SNACK_TAG_SHORT_SIZE, tagLongTotalWidth.value]),
      height: interpolate(activeState.value, [0, 1], [MEAL_SNACK_TAG_SHORT_SIZE, 18]),
      paddingHorizontal: interpolate(activeState.value, [0, 1], [4, 7]),
      paddingVertical: interpolate(activeState.value, [0, 1], [4, 3.5]),
    }));

    const tagShortLabelStyle = useAnimatedStyle(() => ({
      opacity: interpolate(activeState.value, [0, 1], [1, 0], Extrapolation.CLAMP),
      transform: [{ scale: interpolate(activeState.value, [0, 1], [1, 0.82], Extrapolation.CLAMP) }],
    }));

    const tagLongLabelStyle = useAnimatedStyle(() => ({
      opacity: interpolate(activeState.value, [0, 1], [0, 1], Extrapolation.CLAMP),
      transform: [{ scale: interpolate(activeState.value, [0, 1], [0.86, 1], Extrapolation.CLAMP) }],
    }));

    const plusBtnStyle = useAnimatedStyle(() => ({
      opacity: interpolate(activeState.value, [0, 1], [0, 1], Extrapolation.CLAMP),
      transform: [{ scale: plusPulseScale.value }],
    }));

    const quantity_counter_transform_origin = 'right' as const;
    const quantityCounterBgStyle = useAnimatedStyle(() => ({
      transform: [{ scaleX: interpolate(activeState.value, [0.5, 1], [0, 1], Extrapolation.CLAMP) }, { scaleY: interpolate(activeState.value, [0, 1], [0, 1], Extrapolation.CLAMP) }],
      opacity: interpolate(activeState.value, [0.5, 1], [0, 1], Extrapolation.CLAMP),
    }));

    const minusBtnStyle = useAnimatedStyle(() => ({
      opacity: interpolate(activeState.value, [0, 1], [0, 1], Extrapolation.CLAMP),
      transform: [{ scale: minusPulseScale.value }],
    }));

    const showScaledCarbs = nutrition_ui_enabled && isVariableCarbs && item.quantity > 0 && !!onScaledCarbsChange && !selectionDisabled;

    const mealSnackShort = isMeal ? 'M' : 'S';
    const tagWritingDirection = rtl.writingDirection;
    const text_align_style = rtl.leadingMediaCopyTextStyle;

    const macros = useMemo(
      () => ({
        cal: parseMacro(item.calories),
        prot: parseMacro(item.protein),
        carb: parseMacro(item.carbs),
        fat: parseMacro(item.fats),
      }),
      [item.calories, item.protein, item.carbs, item.fats],
    );

    return (
      <AnimatedView style={[styles.fullCardContainer, jellyCardStyle]}>
        <GestureDetector gesture={cardTap} style={{ flex: 1 }}>
          <View
            style={styles.cardTouchableRow}
            onLayout={e => {
              fullWidthRef.value = e.nativeEvent.layout.width;
            }}>
            {/* Plain View keeps image inset; press/radius transforms stay on the image frame only. */}
            <View style={styles.imageSection}>
              <AnimatedView style={[styles.imageInner, imageBorderRadiusStyle, imagePressStyle]}>
                <Image source={{ uri: item.attachment }} style={styles.image} resizeMode="cover" fadeDuration={0} />
                <GestureDetector gesture={expandTap}>
                  <AnimatedView
                    style={[styles.detailExpandBtn,expandBtnStyle, { backgroundColor: appearance === 'light' ? c.surface : 'rgba(0,0,0,0.75)' }]}
                    accessible
                    accessibilityRole="button"
                    accessibilityLabel={t('menu.openItemDetail')}>
                    <SvgIcon name="expand" size={MEAL_SNACK_TAG_EXPAND_ICON_SIZE} color={appearance === 'light' ? c.black : '#fff'} />
                  </AnimatedView>
                </GestureDetector>
                <View style={styles.badgeOverlay} pointerEvents="box-none">
                  <View style={styles.badgeOverlayRow}>
                  {showBadge && (
                      <View style={styles.healthCodeBadge}>
                        <Text style={styles.healthCodeBadgeText}>!</Text>
                      </View>
                    )}
                    <Animated.View style={[styles.tag, tagMorphStyle]}>
                      <Animated.View style={[styles.tagLabelLayer, tagShortLabelStyle]} pointerEvents="none">
                        <Text style={styles.tagText} numberOfLines={1} allowFontScaling={false}>
                          {mealSnackShort}
                        </Text>
                      </Animated.View>
                      <Animated.View style={[styles.tagLabelLayer, tagLongLabelStyle]} pointerEvents="none">
                        <Text style={[styles.tagTextLong, { writingDirection: tagWritingDirection }]} numberOfLines={1} ellipsizeMode="tail" allowFontScaling={false}>
                          {mealSnackLong}
                        </Text>
                      </Animated.View>
                    </Animated.View>
                    {show_selection_tag ? (
                      <Animated.View style={[styles.tag, styles.selectionTag, selectionBadgeStyle]}>
                        <Text style={styles.tagText}>#{selection_n}</Text>
                      </Animated.View>
                    ) : null}
               
                  </View>
            
                </View>
              </AnimatedView>
            </View>
            <View style={styles.contentSection}>
              <View
                style={{ flex: 1, minWidth: 0, minHeight: 0 }}
                onLayout={e => {
                  contentWidthRef.value = e.nativeEvent.layout.width;
                }}>
                <View style={styles.topRow}>
                  <View style={styles.titleCell}>
                    <Text style={[styles.title, text_align_style]} numberOfLines={1} ellipsizeMode="tail">
                      {getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name}
                    </Text>
                  </View>
                  {showScaledCarbs ? (
                    <View style={styles.scaledCarbsColumn}>
                      <Clickable
                        onPress={() => {
                          triggerTactileHaptic('press');
                          bumpScaledCarbs(-5);
                        }}
                        style={styles.scaledCarbsBtn}
                        hitSlop={{ top: 10, bottom: 10, left: 8, right: 4 }}
                        accessibilityLabel={t('menu.scaledCarbs') + ' minus'}
                        accessibilityRole="button">
                        <View style={styles.scaledCarbsBtnInner}>
                          <Text style={styles.scaledCarbsBtnText}>−</Text>
                        </View>
                      </Clickable>
                      <View
                        style={styles.scaledCarbValueContainer}
                        accessibilityLabel={`${formatNumber(displayed_scaled_carbs)} ${t('menu.scaledCarbs')}`}>
                        <Text style={styles.scaledCarbsTitleText}>{t('menu.scaledCarbs')}</Text>
                        <Text style={styles.scaledCarbsAmountText}>{`${formatNumber(displayed_scaled_carbs)}`}</Text>
                      </View>
                      <Clickable
                        onPress={() => {
                          triggerTactileHaptic('press');
                          bumpScaledCarbs(5);
                        }}
                        style={styles.scaledCarbsBtn}
                        hitSlop={{ top: 10, bottom: 10, left: 4, right: 8 }}
                        accessibilityLabel={t('menu.scaledCarbs') + ' plus'}
                        accessibilityRole="button">
                        <View style={styles.scaledCarbsBtnInner}>
                          <Text style={styles.scaledCarbsBtnText}>+</Text>
                        </View>
                      </Clickable>
                    </View>
                  ) : null}
                </View>
                <View style={styles.textArea}>
                  <Text style={[styles.description, text_align_style]} numberOfLines={3}>
                    {getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') || item.description || t('menu.freshlyPrepared')}
                  </Text>
                </View>
              </View>
              <AnimatedView style={styles.bottomRow}>
                {nutrition_ui_enabled ? (
                  <AnimatedView style={styles.macroGroup}>
                    <MacroPoint value={macros.cal} label="Kcal" color={c.calorie} cardStyles={styles} />
                    <MacroPoint value={macros.fat} label="F" color={c.fats} cardStyles={styles} />
                    <MacroPoint value={macros.prot} label="P" color={c.protein} cardStyles={styles} />
                    <MacroPoint value={macros.carb} label="C" color={c.carbs} cardStyles={styles} />
                  </AnimatedView>
                ) : (
                  <View style={{ flex: 1 }} />
                )}
                <View style={styles.bottomRowRight}>
                  {item.quantity >= 1 && (
                    <AnimatedView style={styles.quantityCounterGroup}>
                      <AnimatedView style={styles.quantityCounter}>
                        <AnimatedView style={[styles.quantityCounterBg, quantityCounterBgStyle, { transformOrigin: quantity_counter_transform_origin }]} />
                        <AnimatedView style={[styles.quantityStepperSide, minusBtnStyle, minusNudge.nudgeStyle]}>
                          <GestureDetector gesture={minusNudge.gesture}>
                            <Animated.View accessible accessibilityRole="button" accessibilityLabel={t('menu.decreaseQuantity')} style={{ alignItems: 'center', justifyContent: 'center' }}>
                              <Text style={styles.quantityItem}>-</Text>
                            </Animated.View>
                          </GestureDetector>
                        </AnimatedView>
                        <Text style={styles.quantityItem}>{formatNumber(item.quantity)}</Text>
                        <AnimatedView style={[styles.quantityStepperSide, plusBtnStyle, plusNudge.nudgeStyle]}>
                          <GestureDetector gesture={plusNudge.gesture}>
                            <Animated.View accessible accessibilityRole="button" accessibilityLabel={t('menu.increaseQuantity')} style={{ alignItems: 'center', justifyContent: 'center' }}>
                              <Text style={styles.quantityItem}>+</Text>
                            </Animated.View>
                          </GestureDetector>
                        </AnimatedView>
                      </AnimatedView>
                    </AnimatedView>
                  )}
                </View>
              </AnimatedView>
            </View>
          </View>
        </GestureDetector>
      </AnimatedView>
    );
  },
  menuFlowItemCardPropsAreEqual,
);
